// The Overview's Labor Hours workbook (the labor graph's Export, and the
// VoltaGrid Export zip): a Graph tab with the labor graph as an Excel chart,
// then an Hours tab with one row per measure and one column per week.
//
// The Hours tab is laid out to read at a glance: the title, the header right
// under it and frozen with the measure names, the measures grouped under
// shaded section rows, thousands separators, zero weeks left blank, a Total
// column for the weekly rows, and this week's column shaded. The labor graph's
// Upload reads the same file back: it takes the Hours tab and finds the
// 'Metric' header row and the 'Planned Weekly Hours' row wherever they sit
// (plannedHoursFromSheet), so the formatting does not break the round trip.
// Week headers stay 'M/D/YY' text for that reason.

import ExcelJS from 'exceljs';
import moment from 'moment';
import { addChart, comboChartXml } from './xlsxChart';

const NAVY = 'FF1D518C';
const INK = 'FF0A0A0A';
const MUTED = 'FF525252';
const BAND = 'FFF5F5F5';
const TINT = 'FFE8EEF5';
const LINE = 'FFE5E5E5';

const HOURS = '#,##0;-#,##0;';
const FTE = '#,##0.0;-#,##0.0;';

const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
const hairline = { bottom: { style: 'thin', color: { argb: LINE } } };

// Sections of the Hours tab. Metric labels match the table under the graph.
export function laborSections(crafts) {
    const vg = !!(crafts && crafts.length);
    return [
        { title: 'Weekly hours', fmt: HOURS, total: true, rows: [
            { key: 'actual', label: 'Actual Weekly Hours' },
            { key: 'planned', label: 'Planned Weekly Hours' },
            { key: 'forecasted', label: 'Forecasted Weekly Hours' },
        ] },
        ...(vg ? [
            { title: 'Actual weekly hours by craft', fmt: HOURS, total: true, rows: crafts.map((c) => ({ key: `actualBy.${c.key}`, label: `Actual Weekly Hours - ${c.label}` })) },
            { title: 'Forecasted weekly hours by craft', fmt: HOURS, total: true, rows: crafts.map((c) => ({ key: `forecastedBy.${c.key}`, label: `Forecasted Weekly Hours - ${c.label}` })) },
            { title: 'FTE (weekly hours ÷ 60)', fmt: FTE, total: false, rows: [
                { key: 'actualFte', label: 'Actual FTE (hrs / 60)' },
                { key: 'plannedFte', label: 'Planned FTE (hrs / 60)' },
                { key: 'forecastedFte', label: 'Forecasted FTE (hrs / 60)' },
            ] },
        ] : []),
        { title: 'Cumulative hours', fmt: HOURS, total: false, rows: [
            { key: 'cumCurrentPlanned', label: 'Current Planned Cumulative' },
            { key: 'cumPlanned', label: 'Planned Cumulative Hours' },
            { key: 'cumActual', label: 'Actual Cumulative Hours' },
            { key: 'cumForecast', label: 'Forecasted Cumulative Hours' },
        ] },
    ];
}

const valueOf = (tr, key) => {
    const [a, b] = key.split('.');
    const v = b ? tr?.[a]?.[b] : tr?.[a];
    return v === undefined || v === null || v === '' || Number.isNaN(Number(v)) ? null : Number(v);
};

// The title in row 1; what follows starts right under it.
function heading(ws, title) {
    ws.getCell('A1').value = title;
    ws.getCell('A1').font = { bold: true, size: 14, color: { argb: INK } };
    ws.getRow(1).height = 22;
}

function headerRow(ws, rowNo, labels, shadeCol) {
    const row = ws.getRow(rowNo);
    labels.forEach((label, i) => {
        const cell = row.getCell(i + 1);
        cell.value = label;
        cell.fill = solid(NAVY);
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: i === 0 ? 'left' : 'center', vertical: 'middle' };
        if (i + 1 === shadeCol) cell.border = { bottom: { style: 'thick', color: { argb: 'FFD13138' } } };
    });
    row.height = 20;
}

const GRAPH_DATA = 'Graph data';

const colName = (n) => (n > 26 ? colName(Math.floor((n - 1) / 26)) : '') + String.fromCharCode(65 + ((n - 1) % 26));

/**
 * The graph's series, laid out for Excel, from the Chart.js data the Overview
 * draws ({ labels, datasets }), or null without weeks.
 *
 * The Overview sets its stacks side by side in each week (planned | forecast |
 * actual), which an Excel column chart cannot do. So each week gets a
 * category per stack plus an empty one between weeks, and a bar series fills
 * only its own stack's category. Lines take the middle category, the centre
 * of the week's bars, and run on across the others (the chart spans blanks).
 * With the graph's options, the axes end where the graph's do.
 * Returns { slots, rows: [[label, ...values]], header, bars, lines, yMax,
 * y2Max }, with bars and lines as { name, col, color, ... } over the rows'
 * columns, and an axis's max undefined when Excel should pick it.
 */
export function laborChartLayout(chart) {
    const labels = chart?.labels || [];
    const datasets = chart?.datasets || [];
    if (!labels.length || !datasets.length) return null;
    const isLine = (ds) => ds.type === 'line';
    const barSets = datasets.filter((ds) => !isLine(ds));
    const stacks = [...new Set(barSets.map((ds) => ds.stack ?? ''))];
    const slots = stacks.length + 1;
    const mid = Math.max(0, Math.floor((stacks.length - 1) / 2));
    // Actual first, as the legend under the graph lists them; a stack's own
    // order is kept, so its bars stack bottom to top as on the graph
    const ordered = [...barSets.filter((ds) => ds.stack === 'actual'), ...barSets.filter((ds) => ds.stack !== 'actual')];
    // Lines in the order the graph paints them. Chart.js paints the highest
    // `order` first and, within one order, the last dataset first, so an
    // earlier line lies on top where two meet (Planned Cumulative over
    // Original Cumulative). Excel paints its series first to last.
    const lineSets = datasets.map((ds, i) => ({ ds, i })).filter(({ ds }) => isLine(ds))
        .sort((a, b) => (b.ds.order ?? 0) - (a.ds.order ?? 0) || b.i - a.i)
        .map(({ ds }) => ds);
    const series = [
        ...ordered.map((ds) => ({ ds, slot: stacks.indexOf(ds.stack ?? ''), bar: true })),
        ...lineSets.map((ds) => ({ ds, slot: mid, bar: false })),
    ];
    const rows = [];
    labels.forEach((label, i) => {
        for (let s = 0; s < slots; s++) {
            rows.push([s === 0 ? label : null, ...series.map(({ ds, slot, bar }) => {
                if (slot !== s) return null;
                const v = ds.data?.[i];
                if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return null;
                // a zero week draws no bar; a line keeps its zeros
                return bar && !Number(v) ? null : Number(v);
            })]);
        }
    });
    const named = series.map(({ ds, bar }, j) => ({
        name: ds.label,
        col: j + 2,
        bar,
        color: bar ? ds.backgroundColor : (ds.showLine === false ? (ds.backgroundColor || ds.borderColor) : ds.borderColor),
        dashed: !bar && !!(ds.borderDash && ds.borderDash.length),
        // the trending EAC dot: a point with its value written beside it
        markerOnly: !bar && ds.showLine === false,
        label: !bar && ds.showLine === false,
    }));
    // The weekly axis ends at the graph's fixed max. The cumulative axis has a
    // suggested max, which Chart.js rounds out to its next tick, so it is
    // rounded the same way here
    const scales = chart.options?.scales || {};
    const lineTop = Math.max(0, ...lineSets.flatMap((ds) => (ds.data || []).map(Number).filter(Number.isFinite)));
    const yMax = Number(scales.y?.max) || undefined;
    const y2Max = Number(scales.y1?.max)
        || (Number(scales.y1?.suggestedMax) ? roundedTop(Math.max(Number(scales.y1.suggestedMax), lineTop)) : undefined);
    return { slots, rows, header: ['Week', ...series.map(({ ds }) => ds.label)], bars: named.filter((s) => s.bar), lines: named.filter((s) => !s.bar), yMax, y2Max };
}

// v rounded up to a whole step, the step about a sixth of v and 1, 2, 2.5 or
// 5 times a power of ten: 298,157 -> 300,000 (steps of 50,000)
function roundedTop(v) {
    const raw = v / 6;
    const mag = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw);
    return Math.ceil(v / step) * step;
}

/**
 * The workbook as an xlsx buffer.
 * weeks: ISO dates (week starts); tableRows: laborChartConfig.tableRows;
 * crafts: VG_CRAFTS on the VoltaGrid tab, else empty; job / jobName for the
 * title; today: moment-able; chart: the graph's Chart.js data ({ labels,
 * datasets }, and its options for the axes' ends) for the Graph tab, which is
 * left off without it.
 */
export async function buildLaborWorkbook({ weeks, tableRows, crafts, job, jobName, today, chart }) {
    const wb = new ExcelJS.Workbook();
    const weekLabels = weeks.map((w) => moment(w).format('M/D/YY'));
    const now = moment(today);
    // This week's column (1-based, after the Metric column), shaded
    const thisWeek = weeks.findIndex((w) => now.isSameOrAfter(moment(w), 'day') && now.isBefore(moment(w).add(7, 'days'), 'day'));
    const title = `Labor hours — ${[job, jobName].filter(Boolean).join(' ')}`;
    const layout = laborChartLayout(chart);

    // ── Graph ── (the chart is added once the workbook is written, below)
    if (layout) {
        const gs = wb.addWorksheet('Graph', { views: [{ showGridLines: false }] });
        heading(gs, title);
        for (let c = 1; c <= 16; c++) gs.getColumn(c).width = 10;
        gs.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 1 };
    }

    // ── Hours ──
    const ws = wb.addWorksheet('Hours', { views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }] });
    heading(ws, title);
    const shadeCol = thisWeek >= 0 ? thisWeek + 2 : 0;
    const totalCol = weekLabels.length + 2;
    headerRow(ws, 2, ['Metric', ...weekLabels, 'Total'], shadeCol);
    let r = 3;
    laborSections(crafts).forEach((sec) => {
        const band = ws.getRow(r++);
        for (let c = 1; c <= totalCol; c++) band.getCell(c).fill = solid(BAND);
        band.getCell(1).value = sec.title;
        band.getCell(1).font = { bold: true, color: { argb: MUTED } };
        sec.rows.forEach((m) => {
            const row = ws.getRow(r++);
            row.getCell(1).value = m.label;
            row.getCell(1).alignment = { indent: 1 };
            let sum = 0;
            tableRows.forEach((tr, i) => {
                const v = valueOf(tr, m.key);
                const cell = row.getCell(i + 2);
                cell.value = v;
                cell.numFmt = sec.fmt;
                if (v) sum += v;
            });
            const tot = row.getCell(totalCol);
            tot.value = sec.total ? sum : null;
            tot.numFmt = sec.fmt;
            tot.font = { bold: true };
            for (let c = 1; c <= totalCol; c++) {
                const cell = row.getCell(c);
                cell.border = hairline;
                if (c === shadeCol) cell.fill = solid(TINT);
            }
        });
    });
    ws.getColumn(1).width = 34;
    for (let c = 2; c <= totalCol; c++) ws.getColumn(c).width = 10;
    ws.getColumn(totalCol).width = 12;
    ws.pageSetup = { orientation: 'landscape', printTitlesRow: '2:2', printTitlesColumn: 'A:A' };

    if (!layout) return wb.xlsx.writeBuffer();

    // ── Graph data ── (hidden: the chart's series in its week-slot layout)
    const gd = wb.addWorksheet(GRAPH_DATA, { state: 'hidden' });
    gd.addRow(layout.header);
    layout.rows.forEach((r) => gd.addRow(r));
    gd.getColumn(1).width = 10;
    for (let c = 2; c <= layout.header.length; c++) gd.getColumn(c).width = 14;
    const last = layout.rows.length + 1;
    const ref = (col, a, b) => `'${GRAPH_DATA}'!$${colName(col)}$${a}${b ? `:$${colName(col)}$${b}` : ''}`;
    const series = (s) => ({ ...s, nameRef: ref(s.col, 1), ref: ref(s.col, 2, last), values: layout.rows.map((r) => r[s.col - 1]) });
    const chartXml = comboChartXml({
        categories: { ref: ref(1, 2, last), values: layout.rows.map((r) => r[0]) },
        bars: layout.bars.map(series),
        lines: layout.lines.map(series),
        // bars a fifth of their width apart, a week's bars set off by the empty category
        gapWidth: 20,
        // about 16 week labels across
        labelEvery: layout.slots * Math.max(1, Math.ceil(layout.rows.length / layout.slots / 16)),
        yTitle: 'Weekly Hrs',
        y2Title: 'Cumulative Hrs',
        yMax: layout.yMax,
        y2Max: layout.y2Max,
    });
    return addChart(await wb.xlsx.writeBuffer(), { sheet: 'Graph', chartXml, from: { col: 0, row: 1 }, to: { col: 16, row: 26 }, name: 'Labor Hours' });
}

/**
 * The Planned Weekly row of an uploaded Hours tab, as [{ week: 'M/D/YY', value }].
 * aoa: the sheet as an array of rows (XLSX sheet_to_json with header: 1).
 * Finds the 'Metric' header row wherever it is, so the formatted export
 * (title rows above the header) and an older plain export both read.
 * Returns null when there is no Planned Weekly row.
 */
export function plannedHoursFromSheet(aoa) {
    const rows = aoa || [];
    const h = rows.findIndex((row) => String(row?.[0] ?? '').trim() === 'Metric');
    if (h < 0) return null;
    const planned = rows.slice(h + 1).find((row) => /^Planned Weekly( Hours)?$/.test(String(row?.[0] ?? '').trim()));
    if (!planned) return null;
    const out = [];
    rows[h].forEach((label, c) => {
        if (c === 0) return;
        const week = String(label ?? '').trim();
        const v = planned[c];
        if (moment(week, 'M/D/YY', true).isValid() && v !== '' && v !== undefined && v !== null) out.push({ week, value: v });
    });
    return out;
}
